import { useCallback, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import type { NetworkCallbacks } from '@atlassian/search-client/persist-refresh';

import type { TASK_NAMES } from '../../common/utils/events/task-names';
import { createVitalStatsNetworkCallbacks } from '../../common/utils/network-task-tracker';
import { useAnalytics } from '../analytics';

import { useQuickFindAttributes } from './analytics';

type TaskNameValue = (typeof TASK_NAMES)[keyof typeof TASK_NAMES];

type RealQueryOptions = {
	taskName: TaskNameValue;
	uxTaskId: string;
	/**
	 * Whether this is a pre-query (warmup) request.
	 * When true, the network task is associated with a pre-query UX task rather than a post-query UX task.
	 */
	isPreQuery?: boolean;
};

/**
 * Hook to create network callbacks for tracking PostQuery network requests with VitalStats.
 * Generates a unique task ID per network call and tracks network-level metrics.
 */
export const useQuickFindPostQueryNetworkVitalStats: () => {
	createNetworkCallbacks: (options: RealQueryOptions) => NetworkCallbacks | undefined;
	isEnabled: boolean;
} = () => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes } = useQuickFindAttributes();

	const isEnabled = fg('rovo-search-vitalstats-quickfind-postquery-network');

	const createNetworkCallbacks = useCallback(
		(options: RealQueryOptions): NetworkCallbacks | undefined => {
			if (!isEnabled) {
				return undefined;
			}

			const taskId = crypto.randomUUID();
			const searchSessionId = commonAttributes.searchSessionId || '';
			const taskStartAt = performance.now();

			return createVitalStatsNetworkCallbacks(
				taskId,
				options.taskName,
				searchSessionId,
				fireAnalyticsEvent,
				taskStartAt,
				{
					uxTaskId: options.uxTaskId,
					isPreQuery: options.isPreQuery,
				},
			);
		},
		[isEnabled, commonAttributes.searchSessionId, fireAnalyticsEvent],
	);

	return useMemo(
		() => ({
			createNetworkCallbacks,
			isEnabled,
		}),
		[createNetworkCallbacks, isEnabled],
	);
};
